/**
 * Setup-wizard hooks — Phase 24 / Task 24.8 (DEC-099).
 *
 * Wizard status (per-section completion + gate flags) and company-info CRUD.
 * Gated server-side on MANAGE_REFERENCE_DATA (DISPATCH_MANAGER+).
 */
import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery, useApiMutation, useApiOpts } from '@lodex-pro/web-shared';

export interface WizardStatus {
  readonly sectionsComplete: readonly boolean[]; // length 7
  readonly completionPct: number;
  readonly allComplete: boolean;
  readonly completedAt: string | null;
  readonly lastSectionViewed: number;
  readonly wizardBannerDismissed: boolean;
}

export interface CompanyInfo {
  readonly name: string;
  readonly dotNumber: string | null;
  readonly primaryPhone: string | null;
  readonly primaryEmail: string | null;
  readonly businessAddress: string | null;
  readonly haulerAbbreviation: string | null;
  readonly haulerAbbreviationLocked: boolean;
}

export const WIZARD_STATUS_KEY = ['setup-wizard', 'status'] as const;
export const COMPANY_INFO_KEY = ['setup-wizard', 'company-info'] as const;

export function useWizardStatus(enabled = true): ReturnType<typeof useApiQuery<WizardStatus>> {
  const opts = useApiOpts();
  return useApiQuery<WizardStatus>(opts, '/v1/tenants/setup-wizard-status', {
    queryKey: WIZARD_STATUS_KEY,
    enabled,
    staleTime: 10_000,
  });
}

export interface WizardStatusPatch {
  section?: number;
  complete?: boolean;
  lastSectionViewed?: number;
  bannerDismissed?: boolean;
  allComplete?: boolean;
}

export function useUpdateWizardStatus(): ReturnType<
  typeof useApiMutation<WizardStatusPatch, WizardStatus>
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<WizardStatusPatch, WizardStatus>(
    opts,
    '/v1/tenants/setup-wizard-status',
    'PATCH',
    {
      onSuccess: (data) => {
        qc.setQueryData(WIZARD_STATUS_KEY, data);
      },
    },
  );
}

export function useCompanyInfo(): ReturnType<typeof useApiQuery<CompanyInfo>> {
  const opts = useApiOpts();
  return useApiQuery<CompanyInfo>(opts, '/v1/tenants/company-info', {
    queryKey: COMPANY_INFO_KEY,
    staleTime: 30_000,
  });
}

export interface CompanyInfoPatch {
  name?: string;
  dotNumber?: string | null;
  primaryPhone?: string | null;
  primaryEmail?: string | null;
  businessAddress?: string | null;
  haulerAbbreviation?: string;
}

export function useUpdateCompanyInfo(): ReturnType<
  typeof useApiMutation<CompanyInfoPatch, CompanyInfo>
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<CompanyInfoPatch, CompanyInfo>(opts, '/v1/tenants/company-info', 'PATCH', {
    onSuccess: (data) => {
      qc.setQueryData(COMPANY_INFO_KEY, data);
    },
  });
}

/** The 8 wizard steps (step 8 = Review; sections 1-7 map to completion flags). */
export const WIZARD_STEPS: ReadonlyArray<{ readonly n: number; readonly title: string }> = [
  { n: 1, title: 'Company Info' },
  { n: 2, title: 'Operators & Customers' },
  { n: 3, title: 'Disposal Wells' },
  { n: 4, title: 'Yards' },
  { n: 5, title: 'Trucks & Trailers' },
  { n: 6, title: 'Drivers' },
  { n: 7, title: 'Job Types & Rates' },
  { n: 8, title: 'Review & Launch' },
];
